import React from 'react';
import { motion } from 'framer-motion';
import { ArrowPathIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { fadeIn, slideUp } from '../../../lib/motion';
import CongratulationsTrophyIcon from '../../icons/CongratulationsTrophyIcon';
import type { components } from '../../../types/learnerApi';

type AttemptResponse = components['schemas']['AttemptResponse'];

export interface QuizResultViewProps {
  response: AttemptResponse;
  passingGradePercent?: number | null;
  mode: 'learner' | 'preview';
  /** Failed learners with attempts left get encouragement, not condolences. */
  canRetry?: boolean;
}

const QuizResultView: React.FC<QuizResultViewProps> = ({
  response,
  passingGradePercent,
  canRetry = false,
}) => {
  const isPending = response.status === 'submitted';
  const percentage = response.final_percentage;
  const passed =
    passingGradePercent != null && percentage != null
      ? percentage >= passingGradePercent
      : null;

  const items = response.items ?? {};
  const totalItems = Object.keys(items).length;
  const correctCount = Object.values(items).filter((it) => {
    const score = (it as { score?: number | null }).score ?? 0;
    return score > 0;
  }).length;

  // Banner palette per state
  const tone = isPending
    ? {
        circle: 'bg-warning-500',
        title: 'Awaiting Review',
        subtitle: 'Your submission is being reviewed.',
        score: 'text-warning-500',
        scoreSub: 'text-warning-400',
        bar: 'bg-warning-500',
      }
    : passed === false
      ? {
          circle: 'bg-error-500',
          title: canRetry ? 'Not passed — yet' : 'Not Passed',
          subtitle: canRetry
            ? 'Not quite this time. You can try again!'
            : "Unfortunately, you didn't pass this quiz.",
          score: 'text-error-500',
          scoreSub: 'text-error-400',
          bar: 'bg-error-500',
        }
      : passed === true
        ? {
            circle: 'bg-primary-500',
            title: 'Congratulations!',
            subtitle: 'You passed the quiz!',
            score: 'text-success-500',
            scoreSub: 'text-success-400 dark:text-success-300',
            bar: 'bg-success-500',
          }
        : {
            // No passing grade configured — the score speaks for itself;
            // never congratulate blindly.
            circle: 'bg-primary-500',
            title: 'Quiz completed',
            subtitle: 'Your answers are in.',
            score: 'text-text-primary',
            scoreSub: 'text-text-secondary',
            bar: 'bg-primary-500',
          };

  return (
    <motion.div {...fadeIn} className="w-full space-y-6">
      {/* Result panel — Figma 9140:151371. Stat cards (Correct /
          Incorrect / Grade) intentionally omitted — the "N/M" + "%
          Correct" inside the dashed banner already conveys all three. */}
      <motion.div
        {...slideUp}
        className="rounded-2xl bg-surface-primary p-5 sm:p-6 space-y-4 sm:space-y-5 shadow-[0_0_35px_rgb(var(--color-primary) / 0.35)]"
      >
        {/* Header — trophy + title + subtitle */}
        <div className="flex flex-col items-center gap-2 sm:gap-3">
          <div
            className={`size-16 rounded-full flex items-center justify-center ${tone.circle}`}
          >
            {isPending ? (
              <ClockIcon className="w-9 h-9 text-white" />
            ) : passed === false ? (
              canRetry ? (
                <ArrowPathIcon className="w-9 h-9 text-white" />
              ) : (
                <XMarkIcon className="w-9 h-9 text-white" />
              )
            ) : (
              <CongratulationsTrophyIcon className="w-9 h-9 text-white" />
            )}
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-text-primary text-center leading-tight">
            {tone.title}
          </h2>
          <p className="text-sm text-text-secondary text-center max-w-prose">
            {tone.subtitle}
          </p>
        </div>

        {/* Score banner — dashed border + soft gradient */}
        <div className="relative rounded-2xl border-2 border-dashed border-primary-500 p-4 sm:p-5 overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(90deg, rgb(var(--color-primary) / 0.08) 0%, rgb(var(--color-secondary) / 0.08) 100%)',
            }}
          />
          <div className="relative flex flex-col items-center gap-2">
            {totalItems > 0 ? (
              <p className={`text-3xl sm:text-4xl font-bold leading-none ${tone.score}`}>
                {correctCount}/{totalItems}
              </p>
            ) : null}
            <p className={`text-base sm:text-lg font-semibold ${tone.scoreSub}`}>
              {percentage != null
                ? `${Math.round(percentage)}% Correct`
                : 'No score yet'}
            </p>
            <div className="w-full max-w-[448px] h-2.5 rounded-full bg-neutral-200/70 dark:bg-neutral-800/70 overflow-hidden">
              <div
                className={`h-full rounded-full transition-[width] duration-500 ease-out ${tone.bar}`}
                style={{ width: `${Math.max(0, Math.min(100, percentage ?? 0))}%` }}
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Per-item breakdown — uses the enriched fields the
          `current` / `finalize` endpoints return when the attempt is
          graded (`question_text`, `item_type`, `answer.selected_labels`,
          `score`). The auto-generated OpenAPI type only models the
          minimum subset, so we read through a wider local shape.
          When the learner has passed, the breakdown shares the row with
          a sticky champion mascot at 33% width on lg+ viewports. */}
      {totalItems > 0 && (
        passed === true ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 items-start">
            <div className="sm:col-span-2 space-y-3">
              <BreakdownHeader />
              {renderItems(items, true)}
            </div>
            <div className="hidden sm:block sm:col-span-1">
              <div className="sticky top-6 flex items-center justify-center">
                <img
                  src="/tery/tery-happy-jump-champion.png"
                  alt="Champion"
                  className="w-full max-w-[260px] h-auto select-none pointer-events-none"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <BreakdownHeader />
            {renderItems(items, false)}
          </div>
        )
      )}
    </motion.div>
  );
};

export default QuizResultView;

// ── Sub-components ──────────────────────────────────────────────────────

const BreakdownHeader: React.FC = () => (
  <h4 className="text-sm font-medium text-text-secondary uppercase tracking-wide">
    Question breakdown
  </h4>
);

function renderItems(
  items: Record<string, unknown>,
  bulletAnswers: boolean
): React.ReactNode {
  return Object.entries(items).map(([itemId, raw]) => {
    const record = raw as EnrichedItemRecord;
    const question = record.question_text ?? itemId;
    const lines = formatAnswerLines(record);
    const score = record.score ?? null;
    const correct = score != null && score > 0;
    const submitted = score != null;
    // Tuple-matching always uses bullets so each pair stays on its own
    // line — joining them inline with commas would produce a hard-to-read
    // "A → B, C → D, E → F" string.
    const useBullets = bulletAnswers || record.item_type === 'tuple_matching';
    return (
      <div
        key={itemId}
        className="rounded-xl bg-surface-secondary border border-border p-4 space-y-2"
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-semibold text-text-primary leading-snug">{question}</p>
          {submitted && (
            <span
              className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                correct
                  ? 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300'
                  : 'bg-error-50 text-error-700 dark:bg-error-900/30 dark:text-error-300'
              }`}
            >
              {score} pt{score === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {lines.length > 0 && (
          useBullets ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-text-tertiary">Your answer:</p>
              <ul className="list-disc pl-5 space-y-0.5">
                {lines.map((line, i) => (
                  <li key={i} className="text-xs text-text-secondary leading-snug">
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-text-secondary">
              <span className="font-medium text-text-tertiary">Your answer: </span>
              {lines.join(', ')}
            </p>
          )
        )}
      </div>
    );
  });
}

// ── Enriched-record helpers ──────────────────────────────────────────────

/**
 * Wider local shape for an item record. The auto-generated OpenAPI type
 * only models `answer`, `score`, `submitted_at`; the runtime payload from
 * the graded `current` / `finalize` endpoints also includes
 * `question_text`, `item_type`, and human-readable `selected_labels`
 * inside the answer.
 */
type EnrichedItemRecord = {
  answer?: {
    selected_ids?: string[];
    selected_labels?: string[];
    selected_id?: string;
    selected_label?: string;
    text?: string;
    value?: string | number | boolean | null;
    code?: string;
    ordered_ids?: string[];
    ordered_labels?: string[];
    matches?: Record<string, string>;
    /** Human-readable counterpart to `matches`. Each entry has the
     *  display `left` / `right` text for one matched pair. */
    matches_labels?: Record<string, { left?: string; right?: string }>;
  };
  item_type?: string;
  question_text?: string;
  score?: number | null;
  submitted_at?: string | null;
};

/**
 * Render-friendly answer summary — returns one entry per "thing the
 * learner picked", so the consumer can render each as its own bullet
 * (passed view) or join them inline (single-line view).
 */
function formatAnswerLines(record: EnrichedItemRecord): string[] {
  const a = record.answer;
  if (!a) return [];
  if (Array.isArray(a.selected_labels) && a.selected_labels.length > 0) {
    return a.selected_labels.filter((s): s is string => typeof s === 'string' && !!s);
  }
  if (typeof a.selected_label === 'string' && a.selected_label) {
    return [a.selected_label];
  }
  if (Array.isArray(a.ordered_labels) && a.ordered_labels.length > 0) {
    return a.ordered_labels.filter((s): s is string => typeof s === 'string' && !!s);
  }
  if (typeof a.text === 'string' && a.text.trim()) {
    return [a.text.trim()];
  }
  if (typeof a.code === 'string' && a.code.trim()) {
    const trimmed = a.code.trim();
    return [trimmed.slice(0, 200) + (trimmed.length > 200 ? '…' : '')];
  }
  if (typeof a.value === 'boolean') {
    return [a.value ? 'True' : 'False'];
  }
  if (typeof a.value === 'number') {
    return [String(a.value)];
  }
  // Prefer the human-readable `matches_labels` map. When the backend
  // returns it (even with blank values), we treat that as the
  // authoritative source — falling through to the raw `matches` map
  // would surface opaque `L_pair_…` / `R_pair_…` ids in the UI.
  if (a.matches_labels && Object.keys(a.matches_labels).length > 0) {
    const lines: string[] = [];
    for (const pair of Object.values(a.matches_labels)) {
      const left = (pair?.left ?? '').trim();
      const right = (pair?.right ?? '').trim();
      if (!left && !right) continue;
      lines.push(`${left || '—'} → ${right || '—'}`);
    }
    return lines;
  }
  if (a.matches && Object.keys(a.matches).length > 0) {
    return Object.entries(a.matches).map(([k, v]) => `${k} → ${v}`);
  }
  return [];
}
